import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import * as React from 'react';

import {
  GeneratedOrdersPriceCalculation,
  GeneratedTaxType,
} from '../../../types/generated';
import { ProductPurchaseDetails } from '../../../types/Purchasing';
import { ProductPrice } from '../../ProductPrice/ProductPrice';
import { useCountryCode } from '../../../hooks/useCountryCode';

type Props<T extends ProductPurchaseDetails> = {
  product?: T;
  orderPriceCalculation?: GeneratedOrdersPriceCalculation;
};

type LineProps = {
  className?: string;
  isDiscount?: boolean;
  title: string;
  value: string | React.ReactElement;
};

export const Line: React.FC<LineProps> = ({
  className,
  isDiscount,
  title,
  value,
}) => {
  return (
    <div className={classNames('flex w-full gap-x-3', className)}>
      <span className="flex-1">{title}</span>
      <span className={classNames('shrink-0', isDiscount && 'text-active-4')}>
        {isDiscount ? '- ' : null}
        {value}
      </span>
    </div>
  );
};

const ReviewSummary: React.FC<Props<ProductPurchaseDetails>> = ({
  orderPriceCalculation,
  product,
}) => {
  const { t } = useTranslation('product');

  const [countryCode] = useCountryCode();

  if (!orderPriceCalculation || !product) return null;

  const taxesApply = !product.noTaxCharged;

  return (
    <div className="flex flex-col gap-y-2">
      {orderPriceCalculation.totalDiscount > 0 ? (
        <Line
          isDiscount
          title={`${t('drawers.review.breakdown.totalDiscount')} (${Math.round(
            (orderPriceCalculation.totalDiscount /
              (orderPriceCalculation.breakdown.reduce(
                (acc, item) =>
                  item.title.toLowerCase().includes('frame')
                    ? acc
                    : acc + item.quantity * item.price,
                0,
              ) +
                orderPriceCalculation.totalDiscount)) *
              100,
          )}%)`}
          value={
            <ProductPrice
              inMinorUnit
              price={orderPriceCalculation.totalDiscount}
              skipConvertCurrency
            />
          }
        />
      ) : null}
      <Line
        title={t('drawers.review.breakdown.subTotal')}
        value={
          <ProductPrice
            inMinorUnit
            price={orderPriceCalculation.subtotalItemsPrice}
            skipConvertCurrency
          />
        }
      />
      {product.shippingRequired && (
        <Line
          title={t('drawers.review.shipping')}
          value={
            product.freeShipping ? (
              t('drawers.review.free')
            ) : orderPriceCalculation.subtotalShippingPrice ? (
              <ProductPrice
                inMinorUnit
                price={orderPriceCalculation.subtotalShippingPrice}
                skipConvertCurrency
              />
            ) : (
              t('drawers.review.calculatedAtCheckout')
            )
          }
        />
      )}
      {orderPriceCalculation.taxType === GeneratedTaxType.NoTax &&
        countryCode === 'US' && (
          <Line
            title={t('drawers.review.breakdown.salesTax')}
            value={t('drawers.review.calculatedAtCheckout')}
          />
        )}
      {taxesApply &&
        orderPriceCalculation.subtotalTaxPrice > 0 &&
        orderPriceCalculation.taxRate > 0 && (
          <>
            {orderPriceCalculation.taxType === GeneratedTaxType.Ddp && (
              <Line
                title={t('drawers.review.breakdown.duty')}
                value={
                  <ProductPrice
                    inMinorUnit
                    price={orderPriceCalculation.subtotalTaxPrice}
                    skipConvertCurrency
                  />
                }
              />
            )}
          </>
        )}
      {taxesApply &&
        orderPriceCalculation.subtotalTaxPrice === 0 &&
        orderPriceCalculation.taxRate === 0 &&
        orderPriceCalculation.taxType === GeneratedTaxType.Ddu && (
          <Line
            title={t('drawers.review.breakdown.duty')}
            value={t('drawers.review.breakdown.dutyAtDelivery')}
          />
        )}
      <Line
        className="font-bold"
        title={t('drawers.review.breakdown.total')}
        value={
          <ProductPrice
            inMinorUnit
            price={orderPriceCalculation.totalPrice}
            skipConvertCurrency
            skipTaxCalculation
          />
        }
      />
      {taxesApply &&
        orderPriceCalculation.subtotalTaxPrice > 0 &&
        orderPriceCalculation.taxRate > 0 &&
        orderPriceCalculation.taxType === GeneratedTaxType.Vat && (
          <Line
            className="text-neutral-4"
            title={`${t('drawers.review.breakdown.vat')} (${
              orderPriceCalculation.taxRate
            }%)`}
            value={
              <ProductPrice
                inMinorUnit
                price={orderPriceCalculation.subtotalTaxPrice}
                skipConvertCurrency
                skipTaxCalculation
              />
            }
          />
        )}
    </div>
  );
};

export default ReviewSummary;
